import React, { useState, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { 
  Calculator, 
  DollarSign, 
  TrendingUp, 
  Info, 
  CheckCircle, 
  AlertCircle,
  HelpCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import './IrishSalaryTaxCalculator.css';

// Revenue income-tax and USC bands for a single PAYE employee. These are
// planning estimates; a live Tax Credit Certificate/RPN controls payroll.
export const TAX_BANDS = {
  2024: {
    standardRateBand: 42000, // 20% rate up to this amount
    standardRate: 0.20,
    higherRate: 0.40,
    personalTaxCredit: 1875,
    employeeTaxCredit: 1875,
    uscBands: [
      { limit: 12012, rate: 0.005 },
      { limit: 25760, rate: 0.02 },
      { limit: 70044, rate: 0.04 },
      { limit: Infinity, rate: 0.08 }
    ],
    prsiRate: 0.04025,
    prsiExemptionLimit: 18304,
    uscExemptionLimit: 13000,
  },
  2025: {
    standardRateBand: 44000, // Increased for 2025
    standardRate: 0.20,
    higherRate: 0.40,
    personalTaxCredit: 2000,
    employeeTaxCredit: 2000,
    uscBands: [
      { limit: 12012, rate: 0.005 },
      { limit: 27382, rate: 0.02 },
      { limit: 70044, rate: 0.03 },
      { limit: Infinity, rate: 0.08 }
    ],
    prsiRate: 0.042,
    prsiExemptionLimit: 18304,
    uscExemptionLimit: 13000,
  },
  2026: {
    standardRateBand: 44000,
    standardRate: 0.20,
    higherRate: 0.40,
    personalTaxCredit: 2000,
    employeeTaxCredit: 2000,
    uscBands: [
      { limit: 12012, rate: 0.005 },
      { limit: 28700, rate: 0.02 },
      { limit: 70044, rate: 0.03 },
      { limit: Infinity, rate: 0.08 }
    ],
    prsiRate: 0.042375, // 2026 blended annual estimate; calculation uses weekly rates
    prsiExemptionLimit: 18304,
    uscExemptionLimit: 13000,
  }
};

// Critical Skills Employment Permit threshold
export const CRITICAL_SKILLS_THRESHOLD = 40904;
export const CRITICAL_SKILLS_GRADUATE_THRESHOLD = 36848;

export const TAX_CREDITS = {
  rent: { 2024: 1000, 2025: 1000, 2026: 1000 },
  homeCarer: { 2024: 1800, 2025: 1950, 2026: 1950 },
};

const PRSI_WEEKLY_THRESHOLD = 352;
const PRSI_WEEKLY_CREDIT_LIMIT = 424;
const PRSI_WEEKLY_CREDIT = 12;

export const calculateUSC = (grossIncome, bands) => {
  if (grossIncome <= bands.uscExemptionLimit) return 0;

  let usc = 0;
  let previousLimit = 0;
  for (const band of bands.uscBands) {
    const taxableInBand = Math.max(0, Math.min(grossIncome, band.limit) - previousLimit);
    usc += taxableInBand * band.rate;
    previousLimit = band.limit;
    if (grossIncome <= band.limit) break;
  }
  return usc;
};

export const calculateWeeklyClassAPrsi = (weeklyGross, rate) => {
  if (weeklyGross <= PRSI_WEEKLY_THRESHOLD) return 0;

  const grossPrsi = weeklyGross * rate;
  if (weeklyGross <= PRSI_WEEKLY_CREDIT_LIMIT) {
    const taper = (PRSI_WEEKLY_CREDIT_LIMIT - weeklyGross) /
      (PRSI_WEEKLY_CREDIT_LIMIT - PRSI_WEEKLY_THRESHOLD);
    return Math.max(0, grossPrsi - (PRSI_WEEKLY_CREDIT * taper));
  }
  return grossPrsi;
};

export const calculateClassAPrsi = ({ grossIncome, taxYear, age, receivesStatePension }) => {
  // Class A employee PRSI generally applies from age 16 until 66, and can
  // continue until 70 where the employee is not receiving a contributory
  // State Pension. The explicit toggle avoids guessing from age alone.
  if (age >= 70 || (age >= 66 && receivesStatePension)) return 0;

  const weeklyGross = Math.max(0, grossIncome) / 52;
  if (taxYear === 2026) {
    return (calculateWeeklyClassAPrsi(weeklyGross, 0.042) * 39) +
      (calculateWeeklyClassAPrsi(weeklyGross, 0.0435) * 13);
  }
  return calculateWeeklyClassAPrsi(weeklyGross, taxYear === 2024 ? 0.04025 : 0.042) * 52;
};

// Indian comparison is illustrative and uses simplified FY 2024-25 new-regime slabs.
const calculateIndianTax = (grossINR) => {
  // Simplified Indian tax calculation (FY 2024-25)
  let tax = 0;
  const income = grossINR;
  
  // Standard deduction
  const taxableIncome = Math.max(0, income - 75000);
  
  // New regime slabs
  if (taxableIncome <= 300000) {
    tax = 0;
  } else if (taxableIncome <= 700000) {
    tax = (taxableIncome - 300000) * 0.05;
  } else if (taxableIncome <= 1000000) {
    tax = 20000 + (taxableIncome - 700000) * 0.10;
  } else if (taxableIncome <= 1200000) {
    tax = 50000 + (taxableIncome - 1000000) * 0.15;
  } else if (taxableIncome <= 1500000) {
    tax = 80000 + (taxableIncome - 1200000) * 0.20;
  } else {
    tax = 140000 + (taxableIncome - 1500000) * 0.30;
  }
  
  // 4% cess
  tax = tax * 1.04;
  
  return tax;
};

const IrishSalaryTaxCalculator = () => {
  const [grossSalary, setGrossSalary] = useState(60000);
  const [payFrequency, setPayFrequency] = useState('annual');
  const [taxYear, setTaxYear] = useState(2026);
  const [age, setAge] = useState(30);
  const [receivesStatePension, setReceivesStatePension] = useState(false);
  const [pensionContribution, setPensionContribution] = useState(0);
  const [showTaxCredits, setShowTaxCredits] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  // Calculate Irish taxes
  const taxCalculation = useMemo(() => {
    const bands = TAX_BANDS[taxYear];
    const gross = grossSalary;
    const pension = gross * (pensionContribution / 100);
    const taxableIncome = Math.max(0, gross - pension);
    
    // PAYE (Income Tax)
    let paye = 0;
    if (taxableIncome <= bands.standardRateBand) {
      paye = taxableIncome * bands.standardRate;
    } else {
      paye = (bands.standardRateBand * bands.standardRate) + 
             ((taxableIncome - bands.standardRateBand) * bands.higherRate);
    }
    
    // Apply tax credits
    const totalTaxCredits = bands.personalTaxCredit + bands.employeeTaxCredit;
    paye = Math.max(0, paye - totalTaxCredits);
    
    // USC is assessed on gross income rather than income after pension
    // deductions. Revenue applies the charge to the full income once the
    // annual exemption threshold is exceeded.
    const usc = calculateUSC(gross, bands);

    // Class A employee PRSI is calculated from weekly gross pay, including
    // the tapered weekly credit and the 1 October 2026 rate change.
    const prsi = calculateClassAPrsi({
      grossIncome: gross,
      taxYear,
      age,
      receivesStatePension,
    });
    
    // Total deductions
    const totalTax = paye + usc + prsi + pension;
    const netSalary = gross - totalTax;
    
    return {
      gross,
      taxableIncome,
      paye,
      usc,
      prsi,
      pension,
      totalTax,
      netSalary,
      effectiveRate: (totalTax / gross) * 100,
      monthlyNet: netSalary / 12,
      weeklyNet: netSalary / 52,
      meetsCriticalSkills: gross >= CRITICAL_SKILLS_THRESHOLD
    };
  }, [grossSalary, taxYear, age, pensionContribution, receivesStatePension]);

  // Indian comparison
  const indianComparison = useMemo(() => {
    const eurToInr = 90; // Approximate exchange rate
    const grossINR = grossSalary * eurToInr;
    const indianTax = calculateIndianTax(grossINR);
    const netINR = grossINR - indianTax;
    const netEUR = netINR / eurToInr;
    
    return {
      grossINR,
      indianTax,
      netINR,
      netEUR,
      effectiveRate: (indianTax / grossINR) * 100
    };
  }, [grossSalary]);

  // Chart data
  const chartData = [
    { name: 'PAYE', value: taxCalculation.paye, color: '#ef4444' },
    { name: 'USC', value: taxCalculation.usc, color: '#f59e0b' },
    { name: 'PRSI', value: taxCalculation.prsi, color: '#8b5cf6' },
    { name: 'Pension', value: taxCalculation.pension, color: '#3b82f6' },
    { name: 'Net Salary', value: taxCalculation.netSalary, color: '#10b981' }
  ];

  const formatCurrency = (amount, currency = 'EUR') => {
    if (currency === 'EUR') {
      return `€${amount.toLocaleString('en-IE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const getPayFrequencyAmount = () => {
    switch (payFrequency) {
      case 'monthly':
        return { gross: grossSalary / 12, net: taxCalculation.monthlyNet };
      case 'weekly':
        return { gross: grossSalary / 52, net: taxCalculation.weeklyNet };
      default:
        return { gross: grossSalary, net: taxCalculation.netSalary };
    }
  };

  const payAmounts = getPayFrequencyAmount();

  return (
    <div className="tax-calculator">
      <div className="calculator-header">
        <div className="header-icon">
          <Calculator size={32} />
        </div>
        <h2>Irish Salary Tax Calculator</h2>
        <p className="calculator-subtitle">
          Calculate your take-home salary after Irish taxes (PAYE, USC, PRSI)
        </p>
        <div className="tax-year-badge">
          Tax Year {taxYear}
        </div>
      </div>

      <div className="calculator-body">
        {/* Input Section */}
        <section className="input-section">
          <div className="input-group salary-input">
            <label>
              <DollarSign size={18} />
              Annual Gross Salary
            </label>
            <div className="salary-slider-container">
              <input
                type="range"
                min="20000"
                max="200000"
                step="1000"
                value={grossSalary}
                onChange={(e) => setGrossSalary(Number(e.target.value))}
                className="salary-slider"
              />
              <div className="salary-display">
                {formatCurrency(grossSalary)}
              </div>
            </div>
            <div className="salary-presets">
              {[30000, 40000, 50000, 60000, 75000, 100000].map((preset) => (
                <button
                  key={preset}
                  className={`preset-btn ${grossSalary === preset ? 'active' : ''}`}
                  aria-pressed={grossSalary === preset}
                  onClick={() => setGrossSalary(preset)}
                >
                  {formatCurrency(preset)}
                </button>
              ))}
            </div>
          </div>

          <div className="input-row">
            <div className="input-group">
              <label>
                <TrendingUp size={18} />
                Pay Frequency
              </label>
              <div className="frequency-buttons">
                {['annual', 'monthly', 'weekly'].map((freq) => (
                  <button
                    key={freq}
                    className={`freq-btn ${payFrequency === freq ? 'active' : ''}`}
                    aria-pressed={payFrequency === freq}
                    onClick={() => setPayFrequency(freq)}
                  >
                    {freq.charAt(0).toUpperCase() + freq.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="input-group">
              <label>
                <Info size={18} />
                Tax Year
              </label>
              <div className="year-buttons">
                <button
                  className={`year-btn ${taxYear === 2024 ? 'active' : ''}`}
                  aria-pressed={taxYear === 2024}
                  onClick={() => setTaxYear(2024)}
                >
                  2024
                </button>
                <button
                  className={`year-btn ${taxYear === 2025 ? 'active' : ''}`}
                  aria-pressed={taxYear === 2025}
                  onClick={() => setTaxYear(2025)}
                >
                  2025
                </button>
                <button
                  className={`year-btn ${taxYear === 2026 ? 'active' : ''}`}
                  aria-pressed={taxYear === 2026}
                  onClick={() => setTaxYear(2026)}
                >
                  2026
                </button>
              </div>
            </div>
          </div>

          <div className="input-row">
            <div className="input-group">
              <label>Age (affects PRSI)</label>
              <input
                type="number"
                min="16"
                max="70"
                value={age}
                onChange={(e) => setAge(Number(e.target.value))}
                className="age-input"
              />
              {age >= 66 && (
                <span className="age-note">
                  <CheckCircle size={14} /> PRSI exemption may apply
                </span>
              )}
            </div>

            <div className="input-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={receivesStatePension}
                  onChange={(e) => setReceivesStatePension(e.target.checked)}
                />
                Receiving a contributory State Pension
              </label>
              <span className="input-help">Used to determine whether employee PRSI applies from age 66.</span>
            </div>

            <div className="input-group">
              <label>Pension Contribution (%)</label>
              <div className="pension-input">
                <input
                  type="range"
                  min="0"
                  max="25"
                  step="1"
                  value={pensionContribution}
                  onChange={(e) => setPensionContribution(Number(e.target.value))}
                />
                <span className="pension-value">{pensionContribution}%</span>
              </div>
              {pensionContribution > 0 && (
                <span className="pension-note">
                  Tax relief on {formatCurrency(grossSalary * pensionContribution / 100)}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Critical Skills Indicator */}
        <div className={`critical-skills-banner ${taxCalculation.meetsCriticalSkills ? 'eligible' : 'not-eligible'}`}>
          {taxCalculation.meetsCriticalSkills ? (
            <>
              <CheckCircle size={24} />
              <div className="banner-content">
                <strong>Meets the standard Critical Skills salary threshold</strong>
                <p>Your salary meets the €{CRITICAL_SKILLS_THRESHOLD.toLocaleString()} standard threshold. Occupation-list, qualification and permit conditions still apply; some recent graduates may qualify at €{CRITICAL_SKILLS_GRADUATE_THRESHOLD.toLocaleString()}.</p>
              </div>
            </>
          ) : (
            <>
              <AlertCircle size={24} />
              <div className="banner-content">
                <strong>Below the standard Critical Skills salary threshold</strong>
                <p>The standard threshold is €{CRITICAL_SKILLS_THRESHOLD.toLocaleString()}; some qualifying recent graduates may have a €{CRITICAL_SKILLS_GRADUATE_THRESHOLD.toLocaleString()} threshold. Occupation-list and permit conditions also apply.</p>
              </div>
            </>
          )}
        </div>

        {/* Results Section */}
        <section className="results-section">
          <div className="results-grid">
            {/* Main Result Card */}
            <div className="result-card main-result">
              <h3>Your Take-Home Pay</h3>
              <div className="net-salary">
                {formatCurrency(payAmounts.net)}
                <span className="frequency-label">/{payFrequency}</span>
              </div>
              <div className="gross-to-net">
                Gross: {formatCurrency(payAmounts.gross)} → Net: {formatCurrency(payAmounts.net)}
              </div>
              <div className="effective-rate">
                Effective Tax Rate: <strong>{taxCalculation.effectiveRate.toFixed(1)}%</strong>
              </div>
            </div>

            {/* Tax Breakdown Chart */}
            <div className="result-card chart-card">
              <h3>Tax Breakdown</h3>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ 
                        backgroundColor: 'var(--card-bg, #fff)',
                        border: '1px solid var(--border-color, #e5e7eb)',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-legend">
                {chartData.map((item) => (
                  <div key={item.name} className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: item.color }}></span>
                    <span className="legend-name">{item.name}</span>
                    <span className="legend-value">{formatCurrency(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Detailed Breakdown */}
          <div className="breakdown-table-container">
            <table className="breakdown-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>% of Gross</th>
                </tr>
              </thead>
              <tbody>
                <tr className="gross-row">
                  <td>Gross Salary</td>
                  <td>{formatCurrency(taxCalculation.gross)}</td>
                  <td>100%</td>
                </tr>
                <tr>
                  <td>
                    <span className="tax-label">PAYE (Income Tax)</span>
                    <span className="tax-detail">
                      {taxCalculation.taxableIncome > TAX_BANDS[taxYear].standardRateBand
                        ? `20% up to €${TAX_BANDS[taxYear].standardRateBand.toLocaleString()}, 40% above`
                        : '20% standard rate'}
                    </span>
                  </td>
                  <td className="deduction">{formatCurrency(taxCalculation.paye)}</td>
                  <td>{((taxCalculation.paye / grossSalary) * 100).toFixed(1)}%</td>
                </tr>
                <tr>
                  <td>
                    <span className="tax-label">USC (Universal Social Charge)</span>
                    <span className="tax-detail">0.5% - 8% progressive</span>
                  </td>
                  <td className="deduction">{formatCurrency(taxCalculation.usc)}</td>
                  <td>{((taxCalculation.usc / grossSalary) * 100).toFixed(1)}%</td>
                </tr>
                <tr>
                  <td>
                    <span className="tax-label">PRSI</span>
                    <span className="tax-detail">Class A weekly bands; 2026 rate changes on 1 October</span>
                  </td>
                  <td className="deduction">{formatCurrency(taxCalculation.prsi)}</td>
                  <td>{((taxCalculation.prsi / grossSalary) * 100).toFixed(1)}%</td>
                </tr>
                {pensionContribution > 0 && (
                  <tr>
                    <td>
                      <span className="tax-label">Pension Contribution</span>
                      <span className="tax-detail">{pensionContribution}% of gross</span>
                    </td>
                    <td className="deduction">{formatCurrency(taxCalculation.pension)}</td>
                    <td>{pensionContribution}%</td>
                  </tr>
                )}
                <tr className="total-row">
                  <td>Total Deductions</td>
                  <td className="deduction">{formatCurrency(taxCalculation.totalTax)}</td>
                  <td>{taxCalculation.effectiveRate.toFixed(1)}%</td>
                </tr>
                <tr className="net-row">
                  <td>Net Salary (Take-Home)</td>
                  <td className="net">{formatCurrency(taxCalculation.netSalary)}</td>
                  <td>{(100 - taxCalculation.effectiveRate).toFixed(1)}%</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Pay Frequency Breakdown */}
          <div className="frequency-breakdown">
            <h4>Take-Home by Pay Period</h4>
            <div className="frequency-cards">
              <div className="freq-card">
                <span className="freq-label">Annual</span>
                <span className="freq-amount">{formatCurrency(taxCalculation.netSalary)}</span>
              </div>
              <div className="freq-card">
                <span className="freq-label">Monthly</span>
                <span className="freq-amount">{formatCurrency(taxCalculation.monthlyNet)}</span>
              </div>
              <div className="freq-card">
                <span className="freq-label">Weekly</span>
                <span className="freq-amount">{formatCurrency(taxCalculation.weeklyNet)}</span>
              </div>
            </div>
          </div>
        </section>

        {/* India Comparison */}
        <section className="comparison-section">
          <button 
            className="comparison-toggle"
            aria-expanded={showComparison}
            onClick={() => setShowComparison(!showComparison)}
          >
            <HelpCircle size={18} />
            Compare with Indian Take-Home
            {showComparison ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          
          {showComparison && (
            <div className="comparison-content">
              <div className="comparison-grid">
                <div className="comparison-card ireland">
                  <h4>🇮🇪 Ireland</h4>
                  <div className="comp-gross">
                    <span className="comp-label">Gross Salary</span>
                    <span className="comp-value">{formatCurrency(grossSalary)}</span>
                  </div>
                  <div className="comp-tax">
                    <span className="comp-label">Total Tax</span>
                    <span className="comp-value deduction">-{formatCurrency(taxCalculation.totalTax)}</span>
                  </div>
                  <div className="comp-net">
                    <span className="comp-label">Take-Home</span>
                    <span className="comp-value net">{formatCurrency(taxCalculation.netSalary)}</span>
                  </div>
                  <div className="comp-rate">
                    Effective Rate: {taxCalculation.effectiveRate.toFixed(1)}%
                  </div>
                </div>

                <div className="comparison-card india">
                  <h4>🇮🇳 India (Approx.)</h4>
                  <div className="comp-gross">
                    <span className="comp-label">Gross Salary</span>
                    <span className="comp-value">{formatCurrency(indianComparison.grossINR, 'INR')}</span>
                  </div>
                  <div className="comp-tax">
                    <span className="comp-label">Total Tax</span>
                    <span className="comp-value deduction">-{formatCurrency(indianComparison.indianTax, 'INR')}</span>
                  </div>
                  <div className="comp-net">
                    <span className="comp-label">Take-Home</span>
                    <span className="comp-value net">{formatCurrency(indianComparison.netINR, 'INR')}</span>
                  </div>
                  <div className="comp-rate">
                    Effective Rate: {indianComparison.effectiveRate.toFixed(1)}%
                  </div>
                </div>
              </div>

              <div className="comparison-note">
                <Info size={16} />
                <p>
                  <strong>Note:</strong> Indian tax calculation uses simplified new regime rates. 
                  Actual tax may vary based on deductions, exemptions, and state taxes. 
                  Cost of living differences significantly impact purchasing power.
                </p>
              </div>
            </div>
          )}
        </section>

        {/* Tax Credits Section */}
        <section className="tax-credits-section">
          <button 
            className="credits-toggle"
            aria-expanded={showTaxCredits}
            onClick={() => setShowTaxCredits(!showTaxCredits)}
          >
            <Info size={18} />
            Tax Credits & Reliefs
            {showTaxCredits ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          
          {showTaxCredits && (
            <div className="credits-content">
              <div className="credit-item">
                <h5>Personal Tax Credit</h5>
                <p>€{TAX_BANDS[taxYear].personalTaxCredit.toLocaleString()} - Available to all PAYE workers</p>
              </div>
              <div className="credit-item">
                <h5>Employee (PAYE) Tax Credit</h5>
                <p>€{TAX_BANDS[taxYear].employeeTaxCredit.toLocaleString()} - For PAYE employees</p>
              </div>
              <div className="credit-item">
                <h5>Rent Tax Credit</h5>
                <p>Up to €{TAX_CREDITS.rent[taxYear].toLocaleString()} single (€{(TAX_CREDITS.rent[taxYear] * 2).toLocaleString()} jointly assessed) - claim separately if eligible</p>
              </div>
              <div className="credit-item">
                <h5>Home Carer Tax Credit</h5>
                <p>Up to €{TAX_CREDITS.homeCarer[taxYear].toLocaleString()} - claim separately if qualifying spouse/dependent conditions apply</p>
              </div>
              <div className="credit-item">
                <h5>Pension Relief</h5>
                <p>Tax relief on pension contributions at your marginal rate (20% or 40%)</p>
              </div>
              
              <div className="how-to-claim">
                <h5>How to Claim</h5>
                <ol>
                  <li>Register for myAccount on revenue.ie</li>
                  <li>Update your tax credits in "Manage your tax credits"</li>
                  <li>Changes apply automatically to your payroll</li>
                  <li>Review annually to ensure correct credits</li>
                </ol>
              </div>
            </div>
          )}
        </section>

        {/* Info Footer */}
        <div className="calculator-footer">
          <div className="disclaimer">
            <AlertCircle size={16} />
            <p>
              This calculator provides a standard PAYE employee estimate based on current Irish tax rates.
              Actual tax liability may vary based on individual circumstances. 
              Reduced USC rates and optional credits are not added automatically. Consult Revenue or a tax professional for personalized advice.
            </p>
          </div>
          <div className="last-updated">
            Rules reviewed: 27 August 2026 | Sources: Revenue.ie and gov.ie/DSP
          </div>
        </div>
      </div>
    </div>
  );
};

export default IrishSalaryTaxCalculator;
